/* eslint-disable @typescript-eslint/naming-convention */
import * as v from 'valibot';

/**
 * Worker iframe communication schemas
 */

// Message sent from main window to worker iframe
export const WorkerIframeMessageSchema = v.object({
  workerName: v.string(),
  message: v.string(), // JSON stringified message
});

export type WorkerIframeMessage = v.InferOutput<typeof WorkerIframeMessageSchema>;

// Message sent from worker iframe to main window (same structure)
export const WorkerIframeResponseSchema = WorkerIframeMessageSchema;
export type WorkerIframeResponse = v.InferOutput<typeof WorkerIframeResponseSchema>;

/**
 * Session update message (common across all workers)
 */
export const SessionIdUpdateMessageSchema = v.object({
  topic: v.literal('sessionIdUpdate'),
  sessionId: v.optional(v.string()),
});

export type SessionIdUpdateMessage = v.InferOutput<typeof SessionIdUpdateMessageSchema>;

/**
 * Internal pong message (websocket health check)
 */
export const InternalPongMessageSchema = v.object({
  topic: v.literal('internalPong'),
});

export type InternalPongMessage = v.InferOutput<typeof InternalPongMessageSchema>;

/**
 * Health check request message
 */
export const UpdateHealthCheckMessageSchema = v.object({
  topic: v.literal('updateHealthCheck'),
});

export type UpdateHealthCheckMessage = v.InferOutput<typeof UpdateHealthCheckMessageSchema>;

/**
 * Window health check message with subscription data
 */
export const WindowHealthCheckMessageSchema = v.object({
  topic: v.literal('windowHealthCheck'),
  data: v.optional(v.record(v.string(), v.object({
    topic: v.string(),
    data: v.optional(v.any()),
    // Health-check replay uses the stored subscription options to rebuild the
    // same subscription behavior if a window or worker misses registration.
    subscriptionOptions: v.optional(v.object({
      replayStrategy: v.optional(v.string()),
    })),
  }))),
});

export type WindowHealthCheckMessage = v.InferOutput<typeof WindowHealthCheckMessageSchema>;

/**
 * Subscription-related messages
 */
export const SubscriptionMessageBaseSchema = v.object({
  topicUniqueId: v.string(),
  topic: v.string(),
  messageType: v.union([
    v.literal('subscription'),
    v.literal('unsubscribe'),
  ]),
  data: v.optional(v.any()),
  // Replay configuration travels with subscription creation so the worker can
  // apply the correct late-subscriber behavior.
  subscriptionOptions: v.optional(v.object({
    replayStrategy: v.optional(v.string()),
  })),
  requestId: v.optional(v.string()),
});

export type SubscriptionMessageBase = v.InferOutput<typeof SubscriptionMessageBaseSchema>;

/**
 * Healthcheck message type (sent from worker to client)
 */
export const HealthcheckWorkerMessageSchema = v.object({
  type: v.literal('healthcheck'),
  topicUniqueId: v.string(),
  message: v.object({
    error: v.nullable(v.any()),
    payload: v.string(),
    topicUniqueId: v.string(),
  }),
});

export type HealthcheckWorkerMessage = v.InferOutput<typeof HealthcheckWorkerMessageSchema>;

/**
 * Generic worker message (for websocket data)
 */
export const WorkerDataMessageSchema = v.object({
  type: v.string(),
  topicUniqueId: v.optional(v.string()),
  message: v.optional(v.any()),
  requestId: v.optional(v.string()),
});

export type WorkerDataMessage = v.InferOutput<typeof WorkerDataMessageSchema>;

/**
 * Incoming worker message (parsed from JSON string)
 * Union of all possible worker message types
 */
export const IncomingWorkerMessageSchema = v.union([
  SessionIdUpdateMessageSchema,
  InternalPongMessageSchema,
  UpdateHealthCheckMessageSchema,
  WindowHealthCheckMessageSchema,
  SubscriptionMessageBaseSchema,
  WorkerDataMessageSchema,
]);

export type IncomingWorkerMessage = v.InferOutput<typeof IncomingWorkerMessageSchema>;

/**
 * Queue item for worker backlog
 */
export const WorkerBacklogItemSchema = v.object({
  port: v.any(), // Port cannot be validated with Valibot as it's a native object
  message: IncomingWorkerMessageSchema,
});

export type WorkerBacklogItem = v.InferOutput<typeof WorkerBacklogItemSchema>;

/**
 * Credentials type for worker initialization
 */
export const WorkerCredentialsSchema = v.object({
  credentials: v.optional(v.object({
    sessionId: v.optional(v.string()),
  })),
  sessionId: v.optional(v.string()),
});

export type WorkerCredentials = v.InferOutput<typeof WorkerCredentialsSchema>;

/**
 * Message queue item for Workers.ts
 */
export const MessageQueueItemSchema = v.object({
  workerName: v.string(),
  message: v.string(),
});

export type MessageQueueItem = v.InferOutput<typeof MessageQueueItemSchema>;
